import { Box } from '@mui/material';
import { useOutletContext, useSearchParams } from 'react-router-dom';

import type { AnalyticsOutletContext } from '../../AnalyticsShell';
import AnalyticsTabs from '../../components/AnalyticsTabs';
import AgentDocumentStatuses from '../AgentDocumentStatuses/AgentDocumentStatuses';
import AssessmentCompletionView from '../AssessmentCompletionView/AssessmentCompletionView';
import BackofficeUploadsView from '../BackofficeUploadsView/BackofficeUploadsView';
import CaseStatusTracking from '../CaseStatusTracking/CaseStatusTracking';
import ClientStatusSection from './ClientStatusSection';
import DocumentsUploaded from './DocumentsUploaded';
import MessagesPosted from './MessagesPosted';
import RoleLevelOperations from './RoleLevelOperations';
import StatusMovements from './StatusMovements';

import useTranslate from 'hooks/useTranslate';

type OperationsTabKey =
    | 'fileStatus'
    | 'statusMovements'
    | 'teamOperations'
    | 'documentUploads'
    | 'assessmentProcessing';

const OPERATIONS_TABS: { key: OperationsTabKey; labelKey: string }[] = [
    { key: 'fileStatus', labelKey: 'operationsFileStatus' },
    { key: 'statusMovements', labelKey: 'operationsStatusMovements' },
    { key: 'teamOperations', labelKey: 'operationsTeamOperations' },
    { key: 'documentUploads', labelKey: 'operationsDocumentUploads' },
    { key: 'assessmentProcessing', labelKey: 'assessmentProcessing' },
];

const OperationsView = () => {
    const { from, to, serviceProviderId } = useOutletContext<AnalyticsOutletContext>();
    const [searchParams, setSearchParams] = useSearchParams();
    const { translate } = useTranslate();
    const requestedTab = searchParams.get('operationsTab');
    const activeTab = OPERATIONS_TABS.find((tab) => tab.key === requestedTab)?.key ?? 'fileStatus';

    const setActiveTab = (tab: OperationsTabKey) => {
        setSearchParams(
            (current) => {
                const next = new URLSearchParams(current);
                if (tab === 'fileStatus') {
                    next.delete('operationsTab');
                } else {
                    next.set('operationsTab', tab);
                }
                return next;
            },
            { replace: true }
        );
    };

    return (
        <Box>
            <AnalyticsTabs
                ariaLabel={translate('operationsSections')}
                selected={activeTab}
                onChange={setActiveTab}
                tabs={OPERATIONS_TABS.map((tab) => ({
                    value: tab.key,
                    label: translate(tab.labelKey),
                }))}
            />

            {activeTab === 'fileStatus' && (
                <ClientStatusSection from={from} to={to} serviceProviderId={serviceProviderId} />
            )}
            {activeTab === 'statusMovements' && (
                <StatusMovements from={from} to={to} serviceProviderId={serviceProviderId} />
            )}
            {activeTab === 'teamOperations' && (
                <>
                    <RoleLevelOperations
                        from={from}
                        to={to}
                        serviceProviderId={serviceProviderId}
                    />
                    <DocumentsUploaded from={from} to={to} serviceProviderId={serviceProviderId} />
                    <MessagesPosted from={from} to={to} serviceProviderId={serviceProviderId} />
                </>
            )}
            {activeTab === 'documentUploads' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <BackofficeUploadsView
                        from={from}
                        to={to}
                        serviceProviderId={serviceProviderId}
                    />
                    <CaseStatusTracking from={from} to={to} serviceProviderId={serviceProviderId} />
                    <AgentDocumentStatuses
                        from={from}
                        to={to}
                        serviceProviderId={serviceProviderId}
                    />
                </Box>
            )}
            {activeTab === 'assessmentProcessing' && <AssessmentCompletionView />}
        </Box>
    );
};

export default OperationsView;
